// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Branches page (Stage HRMS-2).
// COMPACT INLINE CRUD — same pattern as Departments. RBAC server-side
// (hrms.branch.*).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Switch, Space, Spin, Table, Tooltip, message } from 'antd';
import {
  PlusOutlined, SaveOutlined, ReloadOutlined, BankOutlined, SearchOutlined
} from '@ant-design/icons';

import { hrBranchesApi } from './hrBranchesApi';
import {
  normalizeBranchRows, sortBranches, validateBranchRows, buildBranchBulkPayload, markDirtyRow
} from './hrBranchesHelpers';
import '../hrms.css';

export default function BranchesPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await hrBranchesApi.list({ limit: 1000 });
      setRows(sortBranches(normalizeBranchRows((out && out.rows) || [])));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load branches.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.branchCode, r.branchName, r.city, r.state]
      .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(() => rows.filter((r) => r._dirty).length, [rows]);
  const patch = (clientRowId, p) => setRows((prev) => markDirtyRow(prev, clientRowId, p));

  const addRow = () => {
    setRows((prev) => [...prev, {
      _clientRowId: `hb-new-${Date.now()}-${prev.length + 1}`, _dirty: true,
      id: null, branchCode: '', branchName: '', shortName: '', city: '', state: '', phone: '', email: '', active: true
    }]);
  };

  const saveAll = async () => {
    if (rows.filter((r) => r._dirty).length === 0) { message.info('Nothing to save.'); return; }
    const { ok, errors } = validateBranchRows(rows);
    if (!ok) { message.error(errors[0].message); return; }
    setSaving(true);
    try {
      const out = await hrBranchesApi.bulkSave(buildBranchBulkPayload(rows, []));
      message.success(`Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save branches.');
    } finally { setSaving(false); }
  };

  const txt = (field, ph, max) => (v, row) => (
    <Input size="small" variant="borderless" value={v} placeholder={ph} maxLength={max}
      onChange={(e) => patch(row._clientRowId, { [field]: e.target.value })} />
  );

  const columns = [
    { title: 'Branch Code', dataIndex: 'branchCode', width: 140, render: txt('branchCode', 'HO', 40) },
    { title: 'Branch Name', dataIndex: 'branchName', width: 220, render: txt('branchName', 'Head Office', 160) },
    { title: 'Short Name', dataIndex: 'shortName', width: 140, render: txt('shortName', 'Optional', 60) },
    { title: 'City', dataIndex: 'city', width: 140, render: txt('city', '', 100) },
    { title: 'State', dataIndex: 'state', width: 140, render: txt('state', '', 100) },
    { title: 'Phone', dataIndex: 'phone', width: 140, render: txt('phone', '', 20) },
    { title: 'Email', dataIndex: 'email', width: 200, render: txt('email', '', 160) },
    { title: 'Active', dataIndex: 'active', width: 84, align: 'center',
      render: (v, row) => <Switch size="small" checked={v !== false} onChange={(c) => patch(row._clientRowId, { active: c })} /> }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><BankOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Branches</span>
            <span className="hr-headstrip__subtitle">Locations employees belong to. Edit any cell — Save All Changes to persist.</span>
          </div>
        </div>
        <Space size={6}>
          <span className="hr-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && <span className="hr-stat-chip hr-stat-chip--dirty"><strong>{dirtyCount}</strong> unsaved</span>}
        </Space>
      </div>

      <div className="hr-toolbar">
        <Input allowClear size="small" prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search branch, city, state…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 300 }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Add Row</Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveAll}
          loading={saving} disabled={saving || dirtyCount === 0}>Save All Changes</Button>
      </div>

      <div className="hr-card">
        <Spin spinning={loading}>
          <Table className="hr-grid" size="small" rowKey="_clientRowId"
            dataSource={visible} columns={columns} pagination={false} sticky
            scroll={{ x: 'max-content' }} rowClassName={(r) => r._dirty ? 'hr-dirty-row' : ''}
            locale={{ emptyText: rows.length === 0 ? 'No branches yet. Click Add Row to start.' : 'No branches match the search.' }} />
        </Spin>
      </div>
    </div>
  );
}
